import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardProjectComponent } from '../../components/projects/card-project/card-project.component';
import { BannerProjectComponent } from '../banner-project/banner-project.component';
import { PrimengModule } from '../../extra-module/primeng.module';
import { trigger, style, transition, animate } from '@angular/animations';
import { CarouselComponent } from "../../components/shared/carousel/carousel.component";
import { ProjectService, Project, OperatingArea } from '../../services/project/project.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Title, Meta } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-projects-page',
  templateUrl: './projects-page.component.html',
  styleUrl: './projects-page.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardProjectComponent,
    BannerProjectComponent,
    PrimengModule,
    CarouselComponent
],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ opacity: 0 }))
      ])
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateY(-30px)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateY(-30px)', opacity: 0 }))
      ])
    ])
  ]
})

export class ProjectsPageComponent implements OnDestroy {
  private readonly siteUrl = environment.SITE_URL;
  modalVisible: boolean = false;
  searchTerm: string = '';
  selectedArea: string | null = null;
  screenWidth: number = 0;
  highlightedImageIndex: number = 0;

  projects: Project[] = [];
  filteredProjects: Project[] = [];
  paginatedProjects: Project[] = [];

  // Pagination properties
  currentPage: number = 1;
  itemsPerPage: number = 9;
  totalPages: number = 0;

  // Debouncing for search
  private searchSubject = new Subject<string>();

  constructor(
    private projectService: ProjectService,
    private titleService: Title,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.setMetaTags();

    // Setup debounced search with 300ms delay
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchValue => {
      this.searchTerm = searchValue;
      this.performFilter();
    });
  }

  ngOnInit() {
    this.projectService.getProjects().subscribe((data) => {
      this.projects = data;
      this.filteredProjects = [...data];
      this.updatePagination();
    });
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }

  // Called on every keystroke
  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  // Actual filtering logic
  private performFilter() {
    this.filteredProjects = this.projects.filter(project => {
      const searchLower = this.searchTerm.toLowerCase().trim();
      const matchesSearch = !searchLower ||
        project.title.toLowerCase().includes(searchLower) ||
        project.client.toLowerCase().includes(searchLower) ||
        project.location.toLowerCase().includes(searchLower);

      const matchesArea = !this.selectedArea || project.businessArea === this.selectedArea;

      return matchesSearch && matchesArea;
    });

    this.currentPage = 1; // Reset to first page when filtering
    this.updatePagination();
  }

  filterProjects() {
    this.performFilter();
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchSubject.next('');
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedArea = null;
    this.filteredProjects = [...this.projects];
    this.currentPage = 1;
    this.updatePagination();
  }

  selectArea(area: OperatingArea | null) {
    this.selectedArea = area;
    this.performFilter();
    this.modalVisible = false;
  }

  // Pagination methods
  updatePagination() {
    this.totalPages = Math.ceil(this.filteredProjects.length / this.itemsPerPage);
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedProjects = this.filteredProjects.slice(startIndex, endIndex);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
      this.scrollToTop();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
      this.scrollToTop();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
      this.scrollToTop();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (this.currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(this.totalPages);
      } else if (this.currentPage >= this.totalPages - 2) {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = this.totalPages - 3; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push(-1); // ellipsis
        for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // ellipsis
        pages.push(this.totalPages);
      }
    }

    return pages;
  }

  private scrollToTop() {
    const element = document.querySelector('.projects-header');
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  get hasResults(): boolean {
    return this.filteredProjects.length > 0;
  }

  isImageHighlighted(index: number): boolean {
    return index === this.highlightedImageIndex;
  }

  setMetaTags(): void {
    const metaTitle = 'Proyectos de construcción en México | Paredes y Asociados';
    const metaDescription = 'Explora proyectos de construcción realizados por Paredes y Asociados en salud, industria, sector público y vivienda en todo México.';
    const url = `${this.siteUrl}/proyectos/`;
    const image = this.toAbsoluteUrl('/assets/images/projects/centro-de-convenciones-oaxaca/33-centro-de-convenciones-oaxaca.webp');

    this.titleService.setTitle(metaTitle);
    this.metaService.updateTag({ name: 'description', content: metaDescription });

    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
    this.metaService.updateTag({ name: 'description', content: metaDescription });
    this.setCanonicalUrl(url);

    this.metaService.updateTag({
      name: 'keywords',
      content: 'Paredes y Asociados, Proyectos de construcción, Portafolio de obras, México'
    });

    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:locale', content: 'es_MX' });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Paredes y Asociados' });

    this.metaService.updateTag({ property: 'og:title', content: metaTitle });
    this.metaService.updateTag({ property: 'og:description', content: metaDescription });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:image', content: image });
    this.metaService.updateTag({ property: 'og:image:alt', content: 'Paredes y Asociados' });

    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: metaTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: metaDescription });
    this.metaService.updateTag({ name: 'twitter:url', content: url });
    this.metaService.updateTag({ name: 'twitter:image', content: image });
  }

  private setCanonicalUrl(url: string): void {
    const head = this.document.head;
    let link = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private toAbsoluteUrl(path: string): string {
    if (!path) {
      return `${this.siteUrl}/assets/images/logos/logo-full.webp`;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return `${this.siteUrl}${path}`;
  }

}
